/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '../services/api';
import TurnstileGate from '../components/security/TurnstileGate';
import useCaptchaConfig from '../hooks/useCaptchaConfig';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaStatus, setCaptchaStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const captchaRef = useRef(null);
  const captchaCfg = useCaptchaConfig();
  const needsCaptcha = captchaCfg.ready && captchaCfg.captchaEnabled;
  const captchaBlocks = needsCaptcha && (!captchaToken || captchaStatus !== 'ready');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (needsCaptcha && !captchaToken) {
      setError('Please complete the security check.');
      return;
    }
    setLoading(true);

    try {
      await api.forgotPassword(email, captchaToken);
      setSuccess(true);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'CAPTCHA_FAILED') {
        setError('Security check failed. Please try again.');
        setCaptchaToken(null);
        if (captchaRef.current) captchaRef.current.reset();
      } else {
        // Don't reveal if email exists
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Check Your Email</h2>
          <p className="text-gray-600 mb-6">
            If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The link will expire in 1 hour. Check your spam folder if you don't see the email.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-cyan-500 text-white py-2 rounded-lg hover:bg-cyan-600 transition-colors font-medium"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/login')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Forgot Password</h2>
            <p className="text-sm text-gray-600">
              Enter your email to reset your password
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="your@email.com"
                required
              />
              <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {needsCaptcha && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Please complete the security check:</p>
              <TurnstileGate
                ref={captchaRef}
                onToken={setCaptchaToken}
                onStatus={setCaptchaStatus}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email || captchaBlocks}
            className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
              email && !loading && !captchaBlocks
                ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
          {error && (
            <p className="text-sm text-red-600 mt-2 text-center">{error}</p>
          )}
        </form>

        <p className="text-center text-sm text-gray-600 mt-6">
          Remember your password?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
